/**
 * Agent pointer state for the browser live view, keyed by tab.
 *
 * Ported from t3code (`apps/web/src/browser/browserPointerStore.ts`, MIT,
 * pin `e3e7cc3f`). Adaptation: forge's runtime does not echo the agent's
 * pointer coordinates, so the store is fed by mapping the thread's browser
 * stream activity (`lastActivityByTabId`) onto pointer events instead of
 * receiving desktop pointer payloads. Events carry fractional positions, and
 * an event without its own position keeps the previous one, so the cursor
 * stays where it last was between actions.
 *
 * @module browserPointerStore
 */
import { create } from "zustand";

import type { BrowserThreadState } from "~/browserStateStore";

/** Who last acted on the page; drives how visible the cursor rests. */
export type BrowserController = "human" | "agent" | "none";

export type BrowserPointerPhase = "move" | "click";

export interface BrowserAgentPointerEvent {
  readonly tabId: string;
  /** Fractional position on the mirrored viewport, 0 to 1 on each axis. */
  readonly x: number;
  readonly y: number;
  readonly phase: BrowserPointerPhase;
  /** Monotonic per tab; a new sequence restarts the cursor's active window. */
  readonly sequence: number;
}

/** One entry of the thread's browser activity feed. */
export type BrowserThreadActivity = BrowserThreadState["lastActivityByTabId"][string];

/**
 * Commands that interact at a point on the page. They read as the cursor
 * pressing or scrubbing; everything else is movement or state change.
 */
const CLICK_LIKE_BROWSER_COMMANDS: ReadonlySet<string> = new Set([
  "click",
  "clickPoint",
  "scroll",
  "wheel",
]);

export function browserPointerPhaseForCommand(
  commandType: string | undefined,
): BrowserPointerPhase {
  return commandType !== undefined && CLICK_LIKE_BROWSER_COMMANDS.has(commandType)
    ? "click"
    : "move";
}

export function browserControllerFromActivity(
  activity: BrowserThreadActivity | null | undefined,
): BrowserController {
  if (!activity) return "none";
  return activity.actor;
}

/**
 * Maps one activity entry onto a pointer event. The position is the previous
 * event's, or the viewport center when there is none, because forge's runtime
 * reports what the agent did, not where its pointer is.
 */
export function browserPointerEventFromActivity(input: {
  readonly tabId: string;
  readonly activity: BrowserThreadActivity;
  readonly previous: BrowserAgentPointerEvent | null;
}): BrowserAgentPointerEvent {
  const { tabId, activity, previous } = input;
  return {
    tabId,
    x: previous?.x ?? 0.5,
    y: previous?.y ?? 0.5,
    phase: browserPointerPhaseForCommand(activity.commandType),
    sequence: (previous?.sequence ?? 0) + 1,
  };
}

interface BrowserPointerStoreState {
  readonly byTabId: Record<string, BrowserAgentPointerEvent>;
  readonly apply: (event: BrowserAgentPointerEvent) => void;
  readonly clear: (tabId: string) => void;
}

export const useBrowserPointerStore = create<BrowserPointerStoreState>()((set) => ({
  byTabId: {},
  apply: (event) =>
    set((state) => ({
      byTabId: {
        ...state.byTabId,
        [event.tabId]: event,
      },
    })),
  clear: (tabId) =>
    set((state) => {
      if (!(tabId in state.byTabId)) return state;
      const { [tabId]: _removed, ...byTabId } = state.byTabId;
      return { byTabId };
    }),
}));
